import { Howl } from 'howler';
import type { RuntimeWorldEvent, WorldEventType } from '../../overlay/WorldEventEffects';

export type BackgroundMusicId = 'off' | 'whispers-old-road' | 'taverns-secret';

export interface AudioDirectorState {
  readonly available: boolean;
  readonly backgroundMusic: BackgroundMusicId;
  readonly backgroundPlaying: boolean;
  readonly backgroundVolume: number;
  readonly activeEvent: WorldEventType | null;
  readonly eventTrackPlaying: boolean;
  readonly eventVolume: number;
}

declare global {
  interface Window {
    StreamKingdomsAudioState?: () => AudioDirectorState;
  }
}

const BACKGROUND_VOLUME = 0.12;
const BACKGROUND_DUCKED_VOLUME = 0.045;
const EVENT_VOLUME = 0.42;
const FADE_DURATION_MS = 900;

const BACKGROUND_TRACKS: Readonly<Record<Exclude<BackgroundMusicId, 'off'>, string>> = {
  'whispers-old-road': 'audio/background/whispers-of-the-old-road.mp3',
  'taverns-secret': 'audio/background/the-taverns-secret.mp3',
};

/**
 * Add future event music here. The event channel automatically plays louder
 * and ducks the background track while the mapped event remains active.
 */
const EVENT_TRACKS: Readonly<Partial<Record<WorldEventType, string>>> = {};

export class AudioDirector {
  private readonly assetBaseUrl: string;
  private background: Howl | null = null;
  private backgroundMusic: BackgroundMusicId;
  private eventTrack: Howl | null = null;
  private backgroundSoundId: number | null = null;
  private eventSoundId: number | null = null;
  private activeEvent: WorldEventType | null = null;
  private unlockBound = false;

  constructor(backgroundMusic: BackgroundMusicId = 'whispers-old-road') {
    this.assetBaseUrl = resolveAssetBaseUrl();
    this.backgroundMusic = backgroundMusic;
    this.background = this.createBackgroundTrack(backgroundMusic);
  }

  start(): void {
    if (!this.background || this.background.playing()) return;
    this.background.volume(BACKGROUND_VOLUME);
    this.backgroundSoundId = this.background.play();
    this.bindUnlock();
  }

  setBackgroundMusic(backgroundMusic: BackgroundMusicId): void {
    if (backgroundMusic === this.backgroundMusic) return;

    this.stopEventTrack();
    this.background?.unload();
    this.background = null;
    this.backgroundSoundId = null;
    this.backgroundMusic = backgroundMusic;

    if (backgroundMusic === 'off') return;

    this.background = this.createBackgroundTrack(backgroundMusic);
    this.start();
    if (this.activeEvent && EVENT_TRACKS[this.activeEvent]) {
      this.startEventTrack(this.activeEvent);
    }
  }

  private createBackgroundTrack(backgroundMusic: BackgroundMusicId): Howl | null {
    if (backgroundMusic === 'off') return null;
    return new Howl({
      src: [this.assetUrl(BACKGROUND_TRACKS[backgroundMusic])],
      autoplay: false,
      html5: true,
      loop: true,
      preload: true,
      volume: BACKGROUND_VOLUME,
      onloaderror: (_id, error) => console.warn('Stream Kingdoms Hintergrundmusik konnte nicht geladen werden.', error),
      onplayerror: () => this.bindUnlock(),
    });
  }

  setWorldEvent(event: RuntimeWorldEvent | null): void {
    const nextType = event?.type ?? null;
    if (nextType === this.activeEvent) return;
    this.activeEvent = nextType;
    this.stopEventTrack();

    if (!this.background) return;

    if (!nextType) {
      this.restoreBackground();
      return;
    }

    const eventPath = EVENT_TRACKS[nextType];
    if (!eventPath) {
      this.restoreBackground();
      return;
    }

    this.startEventTrack(nextType);
  }

  private startEventTrack(eventType: WorldEventType): void {
    const eventPath = EVENT_TRACKS[eventType];
    if (!eventPath || !this.background) return;

    this.duckBackground();
    const track = new Howl({
      src: [this.assetUrl(eventPath)],
      autoplay: false,
      html5: true,
      loop: true,
      preload: true,
      volume: EVENT_VOLUME,
      onloaderror: (_id, error) => {
        console.warn(`Stream Kingdoms Event-Musik für ${eventType} konnte nicht geladen werden.`, error);
        this.restoreBackground();
      },
      onplayerror: () => this.bindUnlock(),
    });
    this.eventTrack = track;
    this.eventSoundId = track.play();
    this.bindUnlock();
  }

  getState(): AudioDirectorState {
    return {
      available: Boolean(this.background),
      backgroundMusic: this.backgroundMusic,
      backgroundPlaying: Boolean(this.background?.playing(this.backgroundSoundId ?? undefined)),
      backgroundVolume: this.background?.volume() ?? 0,
      activeEvent: this.activeEvent,
      eventTrackPlaying: Boolean(this.eventTrack?.playing(this.eventSoundId ?? undefined)),
      eventVolume: this.eventTrack?.volume() ?? 0,
    };
  }

  destroy(): void {
    this.stopEventTrack();
    this.background?.unload();
  }

  private duckBackground(): void {
    if (!this.background) return;
    this.background.fade(this.background.volume(), BACKGROUND_DUCKED_VOLUME, FADE_DURATION_MS, this.backgroundSoundId ?? undefined);
  }

  private restoreBackground(): void {
    if (!this.background) return;
    if (!this.background.playing()) this.start();
    this.background.fade(this.background.volume(), BACKGROUND_VOLUME, FADE_DURATION_MS, this.backgroundSoundId ?? undefined);
  }

  private stopEventTrack(): void {
    const track = this.eventTrack;
    if (!track) return;
    const id = this.eventSoundId ?? undefined;
    track.fade(track.volume(), 0, FADE_DURATION_MS, id);
    track.once('fade', () => {
      track.stop(id);
      track.unload();
    });
    this.eventTrack = null;
    this.eventSoundId = null;
  }

  private bindUnlock(): void {
    if (this.unlockBound) return;
    this.unlockBound = true;
    const unlock = (): void => {
      this.unlockBound = false;
      this.start();
      if (this.eventTrack && !this.eventTrack.playing()) {
        this.eventSoundId = this.eventTrack.play();
      }
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
  }

  private assetUrl(path: string): string {
    return new URL(path, this.assetBaseUrl).href;
  }
}

function resolveAssetBaseUrl(): string {
  const runtimeScript = Array.from(document.scripts).find((script) => script.src.includes('/assets/pixi-runtime/pixi-overlay'));
  if (runtimeScript?.src) return new URL('../', runtimeScript.src).href;
  return new URL('assets/', window.location.href).href;
}
